import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useEntitlements } from '@/hooks/useEntitlements';
import { SURFACES, surfaceEntitled, surfaceForPath } from '@/surfaces';
import SurfaceNotEnabled from '@/pages/SurfaceNotEnabled';

/**
 * Renders a surface's page only when the acting org has the surface (#1160);
 * otherwise the "not enabled — contact Jeket" page. The header and sidebar
 * already hide it (visibleSurfaces); this covers a URL reached directly. The
 * BFF refuses the surface's API either way.
 *
 * While the entitlements load, a gated surface's page waits rather than
 * mounting and calling APIs that may answer 403. A failed read shows the
 * page: the default is on, and the BFF has the last word.
 */
export default function SurfaceGate({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const { entitlements, isLoading } = useEntitlements();
  const surface = surfaceForPath(SURFACES, pathname);

  if (!surface?.entitlement || user?.role === 'superuser') return <>{children}</>;
  if (isLoading) return <p className="p-8 text-sm text-silver-muted">Loading…</p>;
  if (!surfaceEntitled(surface, user, entitlements?.surfaces)) {
    return <SurfaceNotEnabled surface={surface} orgId={entitlements?.orgId} />;
  }
  return <>{children}</>;
}
